import { useState } from 'react';
import { OnboardingBackdrop } from './OnboardingLayout';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * NewWorkspace page — chooser for logged-in users creating a new workspace.
 *
 * Reached from suite apps' "Create New Workspace" button (via Central's
 * `uiHints.createWorkspaceUrl = <baseUrl>/onboarding/new`).
 *
 * Before this redesign, the page silently auto-created a workspace and
 * routed to /onboarding/type without setting `onboardingMode`. That caused
 * /onboarding/type to render the business-default UI even when the user's
 * intent was personal. This page is now the top-level Personal-vs-Business
 * decision for logged-in workspace creation — the same decision that
 * `/start/personal` and `/start/business` represent for landing-page-style
 * entry.
 *
 * On click, the chooser:
 *   1. POST /api/onboarding/create-workspace — creates a new workspace
 *      and switches the session to it (per backend handler). When it
 *      REUSES the user's in-progress workspace (`reused: true`), step 2 is
 *      skipped: StartEntry resumes it instead of resetting it.
 *   2. POST /api/onboarding/mode  — persists the chosen mode (new workspace).
 *   3. navigate('/start/{mode}', { replace: true }) — reuses StartEntry's
 *      tested mode-setting + step-routing logic. StartEntry's POST mode
 *      is idempotent and re-asserts the value harmlessly.
 *
 * Why route through /start/{mode} rather than direct to /onboarding/type:
 * keeps a single source of truth for mode→step routing. When future PRs
 * (PR B in the audit) change /start/{mode} to skip the now-redundant
 * /onboarding/type screen, this chooser inherits that change for free.
 */

type ChosenMode = 'personal' | 'business';

interface ModeOption {
  value: ChosenMode;
  title: string;
  description: string;
  icon: JSX.Element;
}

const modeOptions: ModeOption[] = [
  {
    value: 'business',
    title: 'Business workspace',
    description:
      'For your company, clients, team, operations, sales, and growth.',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
        />
      </svg>
    ),
  },
  {
    value: 'personal',
    title: 'Personal workspace',
    description:
      'For your life, home, goals, schedule, relationships, and personal productivity.',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
        />
      </svg>
    ),
  },
];

export function NewWorkspacePage() {
  const [error, setError] = useState('');
  const [busyMode, setBusyMode] = useState<ChosenMode | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continue') || '';

  async function choose(mode: ChosenMode) {
    if (busyMode) return;
    setBusyMode(mode);
    setError('');

    try {
      // 1. Create the new workspace (and switch the session to it).
      const createRes = await fetch('/api/onboarding/create-workspace', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: mode === 'personal' ? 'My Personal Space' : 'New Workspace',
          // The user clicked a workspace card: a deliberate create, which the
          // backend's invited-member guard must not block (ONB-03).
          explicit: true,
        }),
      });

      if (createRes.status === 401) {
        // No valid session — go to login, then back here.
        const returnUrl = continueUrl
          ? `/onboarding/new?continue=${encodeURIComponent(continueUrl)}`
          : '/onboarding/new';
        navigate(`/auth/login?continue=${encodeURIComponent(returnUrl)}`);
        return;
      }

      const createData = await createRes.json().catch(() => ({}));
      if (!createRes.ok) {
        setError(createData.error || 'Failed to create workspace');
        return;
      }

      const target = continueUrl
        ? `/start/${mode}?continue=${encodeURIComponent(continueUrl)}`
        : `/start/${mode}`;

      // RESUME A REUSED WORKSPACE — never re-set its mode here (ONB-01).
      // create-workspace hands back the user's existing onboarding-incomplete
      // workspace, which may be several steps in. The unconditional POST /mode
      // below reset it to the first step; that is how signing in after
      // verifying an email threw people back to setup. StartEntry is the one
      // place that decides: same mode → resume at the recorded step; other
      // mode with no progress → switch; other mode WITH progress → ask before
      // starting over.
      if (createData.reused) {
        navigate(target, { replace: true });
        return;
      }

      // 2. Persist the chosen mode on the new workspace.
      const modeRes = await fetch('/api/onboarding/mode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mode }),
      });

      if (!modeRes.ok) {
        const data = await modeRes.json().catch(() => ({}));
        setError(data.error || 'Failed to set onboarding mode');
        return;
      }

      // 3. Hand off to /start/{mode} for the rest of the routing.
      navigate(target, { replace: true });
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setBusyMode(null);
    }
  }

  if (error) {
    return (
      <OnboardingBackdrop>
      <div className="flex flex-1 items-center justify-center p-4">
        <div className="bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5 rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h1>
          <p className="text-sm text-gray-500 mb-6">{error}</p>
          <button
            type="button"
            onClick={() => setError('')}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg shadow-lg shadow-emerald-600/25 font-medium hover:bg-emerald-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
      </OnboardingBackdrop>
    );
  }

  return (
    <OnboardingBackdrop>
      <main className="flex flex-1 items-center justify-center px-4 pb-16">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-6 sm:mb-10">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-emerald-300/90">Let&apos;s get you set up</p>
            <h1 className="mt-3 text-xl sm:text-4xl font-semibold tracking-tight text-white">What are you creating?</h1>
            <p className="mt-3 text-slate-300">Choose the kind of workspace that fits why you&apos;re here.</p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {modeOptions.map((option) => {
              const isBusy = busyMode === option.value;
              const disabled = busyMode !== null;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => choose(option.value)}
                  disabled={disabled}
                  aria-label={option.title}
                  className={`group relative w-full rounded-2xl bg-white p-6 text-left shadow-2xl shadow-black/40 ring-1 ring-black/5 transition-all ${
                    disabled
                      ? 'opacity-60 cursor-not-allowed'
                      : 'hover:-translate-y-0.5 hover:ring-2 hover:ring-emerald-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400'
                  }`}
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 transition-colors group-hover:bg-emerald-600 group-hover:text-white">
                    {option.icon}
                  </div>
                  <div className="mt-5 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-slate-900">{option.title}</h3>
                    {isBusy ? (
                      <span className="text-xs text-emerald-700 font-medium">Setting up…</span>
                    ) : (
                      <span aria-hidden="true" className="text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-emerald-600">→</span>
                    )}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{option.description}</p>
                </button>
              );
            })}
          </div>
        </div>
      </main>
    </OnboardingBackdrop>
  );
}
